import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, euro } from "@/lib/api";
import { downloadReceiptPdf } from "@/lib/pdf";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Crown, Loader2, Calendar, CheckCircle2, XCircle, Sparkles, Gift, Copy, PiggyBank, Users, Target, FileText } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—");

export default function Subscription() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [savings, setSavings] = useState(null);
  const [referral, setReferral] = useState(null);
  const [referralList, setReferralList] = useState([]);
  const [history, setHistory] = useState([]);
  const [goalInput, setGoalInput] = useState("");

  const load = () => api.get("/subscription/me").then((r) => setSub(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/savings/summary").then((r) => { setSavings(r.data); if (r.data.goal) setGoalInput(String(r.data.goal)); }).catch(() => {});
    api.get("/referral/me").then((r) => setReferral(r.data)).catch(() => {});
    api.get("/referral/list").then((r) => setReferralList(r.data)).catch(() => {});
    api.get("/payments/history").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  const saveGoal = async () => {
    const g = parseFloat(goalInput);
    if (isNaN(g) || g < 0) { toast.error("Objectif invalide"); return; }
    try {
      await api.put("/savings/goal", { goal: g });
      setSavings((s) => ({ ...s, goal: g }));
      toast.success("Objectif d'épargne enregistré");
    } catch (e) { toast.error("Erreur"); }
  };

  const printReceipt = async (h) => {
    try {
      await downloadReceiptPdf(h);
      toast.success("Reçu PDF téléchargé");
    } catch (e) {
      toast.error("Impossible de générer le PDF");
    }
  };

  const referralLink = referral ? `${window.location.origin}/premium?ref=${referral.code}` : "";
  const copyReferral = async () => {
    try { await navigator.clipboard.writeText(referralLink); toast.success("Lien de parrainage copié !"); }
    catch (e) { toast.message(referralLink); }
  };

  const cancel = async () => {
    setActing(true);
    try {
      await api.post("/subscription/cancel");
      toast.success("Résiliation programmée en fin de période");
      load();
    } catch (e) { toast.error("Résiliation impossible"); }
    finally { setActing(false); }
  };

  const resume = async () => {
    setActing(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Abonnement réactivé");
      load();
    } catch (e) { toast.error("Réactivation impossible"); }
    finally { setActing(false); }
  };

  const isTrialing = sub?.trial_end && new Date(sub.trial_end) > new Date();

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <h1 className="font-display text-3xl font-800 tracking-tight text-secondary">Mon abonnement</h1>
        <p className="mt-1 text-muted-foreground">Gérez votre formule PanierMoinsCher.</p>

        {loading && <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {!loading && sub && !sub.premium && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-2xl border border-border bg-card p-8 text-center" data-testid="sub-free">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground"><Crown className="h-7 w-7" /></div>
            <h2 className="mt-4 font-display text-xl font-800 text-secondary">Formule gratuite</h2>
            <p className="mt-2 text-sm text-muted-foreground">Débloquez les menus illimités, l'historique complet et les alertes email avec Premium.</p>
            <Button onClick={() => nav("/premium")} className="mt-6 rounded-full" data-testid="sub-upgrade-btn"><Sparkles className="mr-2 h-4 w-4" /> Découvrir Premium</Button>
          </motion.div>
        )}

        {!loading && sub && sub.premium && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="tracing-beam mt-8 rounded-2xl bg-card p-8" data-testid="sub-premium">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white"><Crown className="h-6 w-6" /></div>
                <div>
                  <h2 className="font-display text-xl font-800 text-secondary">PanierMoinsCher Premium</h2>
                  <p className="text-sm text-muted-foreground">2,99 € TTC / mois</p>
                </div>
              </div>
              {isTrialing
                ? <Badge className="bg-accent text-white" data-testid="sub-status">Essai gratuit</Badge>
                : <Badge className="bg-primary text-white" data-testid="sub-status">Actif</Badge>}
            </div>

            <div className="mt-6 space-y-3 rounded-xl border border-border p-4">
              {isTrialing && (
                <div className="flex items-center gap-2 text-sm" data-testid="sub-trial-end">
                  <Calendar className="h-4 w-4 text-accent" />
                  <span className="text-muted-foreground">Fin de l'essai gratuit :</span>
                  <b className="text-secondary">{fmt(sub.trial_end)}</b>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm" data-testid="sub-period-end">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">{sub.cancel_at_period_end ? "Accès jusqu'au" : "Prochain renouvellement"} :</span>
                <b className="text-secondary">{fmt(sub.current_period_end || sub.trial_end)}</b>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {sub.cancel_at_period_end
                  ? <><XCircle className="h-4 w-4 text-destructive" /><span className="text-muted-foreground">Résiliation programmée — pas de renouvellement</span></>
                  : <><CheckCircle2 className="h-4 w-4 text-primary" /><span className="text-muted-foreground">Renouvellement automatique activé</span></>}
              </div>
            </div>

            {sub.cancel_at_period_end ? (
              <Button onClick={resume} disabled={acting} className="mt-6 w-full rounded-full" data-testid="sub-resume-btn">
                {acting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Réactiver le renouvellement
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="mt-6 w-full rounded-full text-destructive hover:text-destructive" data-testid="sub-cancel-btn">
                    Résilier mon abonnement
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Résilier Premium ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Vous conservez l'accès Premium jusqu'à la fin de la période en cours ({fmt(sub.current_period_end || sub.trial_end)}).
                      Aucun renouvellement ne sera facturé ensuite.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="sub-cancel-abort">Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={cancel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="sub-cancel-confirm">
                      Confirmer la résiliation
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </motion.div>
        )}
        {!loading && savings && (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6" data-testid="savings-badge">
              <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                <PiggyBank className="h-5 w-5" /> Économies ce mois-ci
              </div>
              <p className="mt-2 font-display text-3xl font-800 text-secondary" data-testid="savings-month">{euro(savings.this_month)}</p>
              <p className="text-xs text-muted-foreground">Total cumulé : {euro(savings.all_time)} · {savings.comparisons} comparaison(s)</p>
              <div className="mt-4 border-t border-primary/20 pt-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-secondary"><Target className="h-4 w-4 text-accent" /> Objectif d'épargne mensuel</div>
                {savings.goal ? (
                  <div className="mt-2">
                    <Progress value={Math.min(100, savings.goal ? (savings.this_month / savings.goal) * 100 : 0)} className="h-2" data-testid="goal-progress" />
                    <p className="mt-1 text-xs text-muted-foreground">{euro(savings.this_month)} / {euro(savings.goal)} · {Math.round(savings.goal ? Math.min(100, (savings.this_month / savings.goal) * 100) : 0)}%</p>
                  </div>
                ) : null}
                <div className="mt-2 flex gap-2">
                  <Input type="number" min="0" step="1" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} placeholder="ex: 25" className="h-8 text-sm" data-testid="goal-input" />
                  <Button size="sm" variant="outline" className="rounded-full" onClick={saveGoal} data-testid="goal-save-btn">Définir</Button>
                </div>
              </div>
            </div>

            {referral && (
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="referral-card">
                <div className="flex items-center gap-2 text-sm font-semibold text-secondary">
                  <Gift className="h-5 w-5 text-accent" /> Parrainage — 1 mois offert
                </div>
                <p className="mt-2 text-xs text-muted-foreground">Partagez votre lien : chaque ami qui s'abonne vous offre 1 mois gratuit.</p>
                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 truncate rounded-lg bg-muted px-3 py-2 text-xs text-secondary" data-testid="referral-link">{referralLink}</code>
                  <Button size="icon" variant="outline" className="flex-none rounded-lg" onClick={copyReferral} data-testid="copy-referral-btn"><Copy className="h-4 w-4" /></Button>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {referral.total} filleul(s)</span>
                  <span className="flex items-center gap-1 text-primary"><Gift className="h-3 w-3" /> {referral.months_free} mois offert(s)</span>
                </div>
                {referralList.length > 0 && (
                  <div className="mt-3 space-y-1.5 border-t border-border pt-3" data-testid="referral-list">
                    {referralList.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-xs" data-testid={`referral-item-${i}`}>
                        <span className="text-secondary">{f.friend}</span>
                        {f.credited
                          ? <Badge className="bg-primary text-white">1 mois offert</Badge>
                          : <Badge variant="outline" className="text-muted-foreground">En attente</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {!loading && history.length > 0 && (
          <div className="mt-4 rounded-2xl border border-border bg-card p-6" data-testid="billing-history">
            <div className="flex items-center gap-2 text-sm font-semibold text-secondary"><FileText className="h-5 w-5 text-primary" /> Historique de facturation</div>
            <div className="mt-3 space-y-2">
              {history.map((h, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid={`billing-row-${i}`}>
                  <div>
                    <p className="text-sm font-medium text-secondary">{h.plan}</p>
                    <p className="text-xs text-muted-foreground">{h.date ? new Date(h.date).toLocaleDateString("fr-FR") : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-display font-700 text-secondary">{euro(h.amount)}</span>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => printReceipt(h)} data-testid={`receipt-btn-${i}`}>Reçu PDF</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
